import React, { useState, useEffect, useCallback } from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import {
  ArrowPathIcon,
  BuildingOfficeIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  CircleStackIcon,
  ServerIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathRoundedSquareIcon,
  SwatchIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

const TIERS = ['standard', 'professional', 'enterprise']

function brandingDraftFromTenant(t) {
  return {
    logoUrl: t.logo_url || '',
    brandmarkUrl: t.brandmark_url || '',
    primaryColor: t.primary_color || '',
    secondaryColor: t.secondary_color || '',
    highlightColor: t.highlight_color || '',
  }
}

function hexPreview(hex) {
  if (!hex || !String(hex).trim()) return null
  let h = String(hex).trim()
  if (h.startsWith('#')) h = h.slice(1)
  if (h.length === 3 && /^[0-9a-fA-F]{3}$/i.test(h)) {
    h = h.split('').map((c) => c + c).join('')
  }
  if (!/^[0-9a-fA-F]{6}$/i.test(h)) return null
  return `#${h.toLowerCase()}`
}

const BRANDING_FILE_ACCEPT = 'image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml'

function brandingImageSrc(url) {
  if (!url || !String(url).trim()) return ''
  const u = String(url).trim()
  if (u.startsWith('http://') || u.startsWith('https://')) return u
  const pathOnly = u.startsWith('/') ? u : `/${u}`
  return getLocalApiUrl(pathOnly)
}

// ── DB status badge ──
function DbStatusBadge({ tenant }) {
  if (!tenant.db_name) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
        <CircleStackIcon className="h-3.5 w-3.5" />
        shared
      </span>
    )
  }
  const map = {
    ready: { bg: 'bg-emerald-50 text-emerald-700', icon: CheckCircleIcon, label: 'ready' },
    migrating: { bg: 'bg-amber-50 text-amber-700', icon: ArrowPathRoundedSquareIcon, label: 'migrating' },
    error: { bg: 'bg-red-50 text-red-700', icon: ExclamationTriangleIcon, label: 'error' },
    pending: { bg: 'bg-blue-50 text-blue-600', icon: CircleStackIcon, label: 'pending' },
  }
  const s = map[tenant.db_status] || map.pending
  const Icon = s.icon
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${s.bg}`}>
      <Icon className={`h-3.5 w-3.5 ${tenant.db_status === 'migrating' ? 'animate-spin' : ''}`} />
      {s.label}
    </span>
  )
}

export default function PlatformTenants() {
  const [tenants, setTenants] = useState([])
  const [loading, setLoading] = useState(true)
  const [provisionOpen, setProvisionOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [actionLoading, setActionLoading] = useState({}) // { [groupId]: 'createDb' | 'migrate' | 'health' }
  const [migrateAllLoading, setMigrateAllLoading] = useState(false)
  const [brandingOpen, setBrandingOpen] = useState(null)
  const [brandingDraft, setBrandingDraft] = useState({})
  const [brandingSaving, setBrandingSaving] = useState(null)
  const [brandingUploading, setBrandingUploading] = useState(null)
  const [form, setForm] = useState({
    groupId: '',
    name: '',
    tier: 'standard',
    venueId: '',
    venueName: '',
    adminUsername: '',
    adminPassword: '',
    adminName: '',
    adminEmail: '',
    sendWelcomeEmail: false,
  })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/admin/tenants'))
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setTenants(Array.isArray(data.tenants) ? data.tenants : [])
    } catch (e) {
      toast.error(e.message || 'Failed to load tenants')
      setTenants([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const updateField = (key, value) => setForm((f) => ({ ...f, [key]: value }))

  // ── Provision ──
  const submitProvision = async (e) => {
    e.preventDefault()
    const gid = form.groupId.trim()
    if (!gid || !form.name.trim()) {
      toast.error('GSID and display name are required')
      return
    }
    if (!/^[a-z0-9]{2,16}$/.test(gid)) {
      toast.error('GSID must be 2–16 lowercase letters/numbers')
      return
    }
    const hasAdmin = form.adminUsername.trim() && form.adminPassword
    if (hasAdmin && !form.adminEmail.trim()) {
      toast.error('Admin email is required when creating an org admin user')
      return
    }
    if (form.sendWelcomeEmail && !form.adminEmail.trim()) {
      toast.error('Admin email is required to send a welcome email')
      return
    }
    const body = {
      group: {
        groupId: gid,
        subdomain: gid,
        name: form.name.trim(),
        tier: form.tier,
        status: 'active',
      },
      venues: form.venueId.trim()
        ? [{ venueId: form.venueId.trim(), name: (form.venueName || form.venueId).trim() }]
        : [],
      admin:
        hasAdmin
          ? {
              username: form.adminUsername.trim(),
              password: form.adminPassword,
              name: form.adminName.trim() || form.adminUsername.trim(),
              email: form.adminEmail.trim(),
              send_welcome_email: form.sendWelcomeEmail,
            }
          : undefined,
      createDatabase: true,
    }
    setSubmitting(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/admin/tenants/provision'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)

      if (data.welcomeEmail && !data.welcomeEmail.skipped && !data.welcomeEmail.success) {
        toast.error(`Tenant provisioned, but welcome email failed: ${data.welcomeEmail.error || 'unknown error'}`)
      } else if (data.database?.status === 'ready') {
        toast.success(`Tenant provisioned with dedicated database "${data.database.dbName}"`)
      } else if (data.database?.status === 'error') {
        toast.success('Tenant provisioned, but database creation failed — see warnings')
      } else {
        toast.success('Tenant provisioned')
      }

      setProvisionOpen(false)
      setForm({
        groupId: '', name: '', tier: 'standard',
        venueId: '', venueName: '', adminUsername: '', adminPassword: '',
        adminName: '', adminEmail: '', sendWelcomeEmail: false,
      })
      await load()
    } catch (err) {
      toast.error(err.message || 'Provision failed')
    } finally {
      setSubmitting(false)
    }
  }

  // ── Per-tenant actions ──
  const createTenantDb = async (groupId) => {
    setActionLoading((s) => ({ ...s, [groupId]: 'createDb' }))
    try {
      const res = await authFetch(getLocalApiUrl(`/api/admin/tenants/${groupId}/database`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      toast.success(`Database "${data.dbName}" created for ${groupId}`)
      await load()
    } catch (err) {
      toast.error(err.message || 'Failed to create database')
    } finally {
      setActionLoading((s) => ({ ...s, [groupId]: null }))
    }
  }

  const migrateTenantDb = async (groupId) => {
    setActionLoading((s) => ({ ...s, [groupId]: 'migrate' }))
    try {
      const res = await authFetch(getLocalApiUrl(`/api/admin/tenants/${groupId}/database/migrate`), {
        method: 'POST',
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      const dataLine = data.dataMigration ? ' — business data synced from main DB' : ''
      toast.success(`Migrations applied — schema v${data.schemaVersion}${dataLine}`)
      await load()
    } catch (err) {
      toast.error(err.message || 'Migration failed')
    } finally {
      setActionLoading((s) => ({ ...s, [groupId]: null }))
    }
  }

  const healthCheckTenantDb = async (groupId) => {
    setActionLoading((s) => ({ ...s, [groupId]: 'health' }))
    try {
      const res = await authFetch(getLocalApiUrl(`/api/admin/tenants/${groupId}/database/health`))
      const data = await res.json().catch(() => ({}))
      if (data.status === 'healthy') {
        toast.success(`${groupId} database is healthy`)
      } else {
        toast.error(`${groupId} database: ${data.error || 'unhealthy'}`)
      }
    } catch (err) {
      toast.error(err.message || 'Health check failed')
    } finally {
      setActionLoading((s) => ({ ...s, [groupId]: null }))
    }
  }

  // ── Migrate All ──
  const migrateAll = async () => {
    setMigrateAllLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl('/api/admin/tenants/databases/migrate-all'), {
        method: 'POST',
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      const ok = data.succeeded?.length || 0
      const fail = data.failed?.length || 0
      if (fail > 0) {
        toast.error(`Migrated ${ok} tenants, ${fail} failed`)
      } else if (ok > 0) {
        toast.success(`All ${ok} tenant databases migrated`)
      } else {
        toast('No tenant databases to migrate')
      }
      await load()
    } catch (err) {
      toast.error(err.message || 'Migrate-all failed')
    } finally {
      setMigrateAllLoading(false)
    }
  }

  const tenantsWithDb = tenants.filter((t) => t.db_name)
  const tenantsWithoutDb = tenants.filter((t) => !t.db_name)

  const openBrandingPanel = (t) => {
    setBrandingOpen(t.group_id)
    setBrandingDraft((d) => ({ ...d, [t.group_id]: brandingDraftFromTenant(t) }))
  }

  const setBrandingField = (groupId, key, value) => {
    setBrandingDraft((d) => ({
      ...d,
      [groupId]: { ...(d[groupId] || brandingDraftFromTenant({})), [key]: value },
    }))
  }

  const saveBranding = async (groupId) => {
    const draft = brandingDraft[groupId]
    if (!draft) return
    setBrandingSaving(groupId)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/admin/tenants/${groupId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          logoUrl: draft.logoUrl,
          brandmarkUrl: draft.brandmarkUrl,
          primaryColor: draft.primaryColor,
          secondaryColor: draft.secondaryColor,
          highlightColor: draft.highlightColor,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      toast.success('Branding saved')
      setBrandingOpen(null)
      await load()
    } catch (err) {
      toast.error(err.message || 'Failed to save branding')
    } finally {
      setBrandingSaving(null)
    }
  }

  const uploadBrandingAsset = async (groupId, kind, file) => {
    if (!file) return
    const key = `${groupId}:${kind}`
    setBrandingUploading(key)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await authFetch(
        getLocalApiUrl(
          `/api/admin/tenants/${encodeURIComponent(groupId)}/branding/upload?kind=${encodeURIComponent(kind)}`
        ),
        { method: 'POST', body: fd }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      if (data.url) {
        if (kind === 'brandmark') setBrandingField(groupId, 'brandmarkUrl', data.url)
        else setBrandingField(groupId, 'logoUrl', data.url)
        toast.success(kind === 'brandmark' ? 'Brandmark uploaded' : 'Logo uploaded')
      }
    } catch (err) {
      toast.error(err.message || 'Upload failed')
    } finally {
      setBrandingUploading(null)
    }
  }

  return (
    <div className="min-h-full bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* ── Header ── */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-psg-teal p-3 shadow-lg">
              <BuildingOfficeIcon className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tenants</h1>
              <p className="text-sm text-gray-600">Platform groups, venues, databases, and provisioning</p>
            </div>
          </div>
          <div className="flex gap-2">
            {tenantsWithDb.length > 0 && (
              <button
                type="button"
                onClick={migrateAll}
                disabled={migrateAllLoading}
                className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 shadow-sm hover:bg-amber-100 disabled:opacity-50"
              >
                <ArrowPathRoundedSquareIcon className={`h-4 w-4 ${migrateAllLoading ? 'animate-spin' : ''}`} />
                Migrate all DBs
              </button>
            )}
            <button
              type="button"
              onClick={() => load()}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => setProvisionOpen((o) => !o)}
              className="inline-flex items-center gap-2 rounded-lg bg-psg-teal px-4 py-2 text-sm font-medium text-white shadow hover:opacity-95"
            >
              {provisionOpen ? (
                <ChevronDownIcon className="h-4 w-4" />
              ) : (
                <ChevronRightIcon className="h-4 w-4" />
              )}
              <PlusIcon className="h-4 w-4" />
              Add tenant
            </button>
          </div>
        </div>

        {/* ── Provision form ── */}
        {provisionOpen && (
          <form
            onSubmit={submitProvision}
            className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-4"
          >
            <h2 className="text-lg font-semibold text-gray-900">Provision tenant</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="font-medium text-gray-700">GSID <span className="font-normal text-gray-400">(Group Short ID)</span></span>
                <input
                  required
                  pattern="[a-z0-9]{2,16}"
                  title="2–16 lowercase letters/numbers (e.g. tsg, mtg, wests)"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
                  value={form.groupId}
                  onChange={(e) => updateField('groupId', e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                  placeholder="e.g. tsg, mtg, wests"
                />
                <span className="text-[11px] text-gray-400 mt-0.5 block">Used as subdomain ({form.groupId || '___'}.paysafego.net) and database name</span>
              </label>
              <label className="block text-sm sm:col-span-1">
                <span className="font-medium text-gray-700">Display name</span>
                <input
                  required
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="e.g. Twinstars Gaming"
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-gray-700">Tier</span>
                <select
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={form.tier}
                  onChange={(e) => updateField('tier', e.target.value)}
                >
                  {TIERS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="border-t border-gray-100 pt-4 grid gap-4 sm:grid-cols-2">
              <p className="text-sm font-medium text-gray-700 sm:col-span-2">First venue (optional)</p>
              <label className="block text-sm">
                <span className="text-gray-600">VSID <span className="font-normal text-gray-400">(Venue Short ID)</span></span>
                <input
                  pattern="[a-z0-9][a-z0-9-]{0,62}[a-z0-9]?"
                  title="Lowercase letters, numbers, hyphens (e.g. diggers, main-floor)"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
                  value={form.venueId}
                  onChange={(e) => updateField('venueId', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  placeholder="e.g. diggers, leagues"
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">Venue name</span>
                <input
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={form.venueName}
                  onChange={(e) => updateField('venueName', e.target.value)}
                  placeholder="e.g. Diggers Services Club"
                />
              </label>
            </div>
            <div className="border-t border-gray-100 pt-4 grid gap-4 sm:grid-cols-2">
              <p className="text-sm font-medium text-gray-700 sm:col-span-2">Org admin (optional)</p>
              <label className="block text-sm">
                <span className="text-gray-600">Username</span>
                <input
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={form.adminUsername}
                  onChange={(e) => updateField('adminUsername', e.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">Password</span>
                <input
                  type="password"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={form.adminPassword}
                  onChange={(e) => updateField('adminPassword', e.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">Name</span>
                <input
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={form.adminName}
                  onChange={(e) => updateField('adminName', e.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">
                  Email
                  {form.adminUsername.trim() && form.adminPassword ? (
                    <span className="text-red-600"> *</span>
                  ) : null}
                </span>
                <input
                  type="email"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  value={form.adminEmail}
                  onChange={(e) => updateField('adminEmail', e.target.value)}
                />
              </label>
              <label className="flex items-start gap-2 text-sm sm:col-span-2">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={form.sendWelcomeEmail}
                  onChange={(e) => updateField('sendWelcomeEmail', e.target.checked)}
                />
                <span className="text-gray-700">Send welcome email with login details</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setProvisionOpen(false)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-psg-teal px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {submitting ? 'Provisioning…' : 'Provision'}
              </button>
            </div>
          </form>
        )}

        {/* ── Summary cards ── */}
        {!loading && tenants.length > 0 && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total tenants</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{tenants.length}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Dedicated DBs</p>
              <p className="mt-1 text-2xl font-bold text-emerald-600">{tenantsWithDb.length}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Shared DB</p>
              <p className="mt-1 text-2xl font-bold text-gray-600">{tenantsWithoutDb.length}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">DB errors</p>
              <p className="mt-1 text-2xl font-bold text-red-600">
                {tenants.filter((t) => t.db_status === 'error').length}
              </p>
            </div>
          </div>
        )}

        {/* ── Tenant table ── */}
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">GSID</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Name</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Tier</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Database</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Venues</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Users</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">Loading…</td>
                  </tr>
                )}
                {!loading && tenants.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">No tenants found</td>
                  </tr>
                )}
                {!loading &&
                  tenants.map((t) => {
                    const action = actionLoading[t.group_id]
                    const draft = brandingDraft[t.group_id] || brandingDraftFromTenant(t)
                    const isBrandOpen = brandingOpen === t.group_id
                    return (
                      <React.Fragment key={t.group_id}>
                      <tr className="hover:bg-gray-50/80">
                        <td className="px-4 py-3 font-mono text-psg-teal font-semibold">{t.group_id}</td>
                        <td className="px-4 py-3 text-gray-900">{t.name}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            t.tier === 'enterprise' ? 'bg-purple-50 text-purple-700'
                            : t.tier === 'professional' ? 'bg-blue-50 text-blue-700'
                            : 'bg-gray-100 text-gray-600'
                          }`}>
                            {t.tier || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            t.status === 'active' ? 'bg-emerald-50 text-emerald-700'
                            : t.status === 'trial' ? 'bg-amber-50 text-amber-700'
                            : 'bg-red-50 text-red-700'
                          }`}>
                            {t.status || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <DbStatusBadge tenant={t} />
                            {t.db_name && (
                              <span className="text-[11px] text-gray-400 font-mono truncate max-w-[140px]" title={t.db_name}>
                                {t.db_name}
                              </span>
                            )}
                            {t.db_schema_version > 0 && (
                              <span className="text-[11px] text-gray-400">v{t.db_schema_version}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-700">{t.venue_count ?? '—'}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-gray-700">{t.user_count ?? '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex flex-wrap items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => (isBrandOpen ? setBrandingOpen(null) : openBrandingPanel(t))}
                              title="Tenant logo, brandmark, and theme colours"
                              className={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium ${
                                isBrandOpen
                                  ? 'border-teal-300 bg-teal-50 text-teal-800'
                                  : 'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100'
                              }`}
                            >
                              <SwatchIcon className="h-3.5 w-3.5" />
                              Branding
                            </button>
                            {/* Create DB — only for tenants without one */}
                            {!t.db_name && (
                              <button
                                onClick={() => createTenantDb(t.group_id)}
                                disabled={!!action}
                                title="Create dedicated database"
                                className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                              >
                                {action === 'createDb' ? (
                                  <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <CircleStackIcon className="h-3.5 w-3.5" />
                                )}
                                Create DB
                              </button>
                            )}
                            {/* Migrate — only for tenants with a DB */}
                            {t.db_name && (
                              <button
                                onClick={() => migrateTenantDb(t.group_id)}
                                disabled={!!action || t.db_status === 'migrating'}
                                title="Run pending migrations"
                                className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                              >
                                {action === 'migrate' ? (
                                  <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <ArrowPathRoundedSquareIcon className="h-3.5 w-3.5" />
                                )}
                                Migrate
                              </button>
                            )}
                            {/* Health check — only for tenants with a DB */}
                            {t.db_name && t.db_status === 'ready' && (
                              <button
                                onClick={() => healthCheckTenantDb(t.group_id)}
                                disabled={!!action}
                                title="Test database connectivity"
                                className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                              >
                                {action === 'health' ? (
                                  <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <ServerIcon className="h-3.5 w-3.5" />
                                )}
                                Health
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isBrandOpen && (
                        <tr className="bg-slate-50/90">
                          <td colSpan={8} className="px-4 py-4 border-t border-slate-200">
                            <div className="space-y-4 max-w-4xl">
                              <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                                <PhotoIcon className="h-4 w-4 text-gray-500" />
                                Branding — {t.name} <span className="font-mono font-normal text-gray-500">({t.group_id})</span>
                              </p>
                              <div className="grid gap-4 sm:grid-cols-2">
                                <div className="block text-sm">
                                  <span className="font-medium text-gray-700">Logo</span>
                                  <input
                                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
                                    placeholder="URL or upload below"
                                    value={draft.logoUrl}
                                    onChange={(e) => setBrandingField(t.group_id, 'logoUrl', e.target.value)}
                                  />
                                  <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <input
                                      id={`branding-logo-file-${t.group_id}`}
                                      type="file"
                                      accept={BRANDING_FILE_ACCEPT}
                                      className="sr-only"
                                      disabled={brandingUploading === `${t.group_id}:logo`}
                                      onChange={(e) => {
                                        const f = e.target.files?.[0]
                                        if (f) uploadBrandingAsset(t.group_id, 'logo', f)
                                        e.target.value = ''
                                      }}
                                    />
                                    <label
                                      htmlFor={`branding-logo-file-${t.group_id}`}
                                      className={`inline-flex cursor-pointer items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 ${
                                        brandingUploading === `${t.group_id}:logo` ? 'pointer-events-none opacity-50' : ''
                                      }`}
                                    >
                                      {brandingUploading === `${t.group_id}:logo` ? 'Uploading…' : 'Upload file'}
                                    </label>
                                    <span className="text-[11px] text-gray-400">
                                      Stored in tenant Azure Blob Storage (category tenant-branding)
                                    </span>
                                  </div>
                                  {draft.logoUrl?.trim() && (
                                    <img
                                      src={brandingImageSrc(draft.logoUrl.trim())}
                                      alt=""
                                      className="mt-2 h-10 max-w-[200px] object-contain object-left"
                                      onError={(e) => { e.target.style.display = 'none' }}
                                    />
                                  )}
                                </div>
                                <div className="block text-sm">
                                  <span className="font-medium text-gray-700">
                                    Brandmark <span className="font-normal text-gray-400">(optional icon)</span>
                                  </span>
                                  <input
                                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
                                    placeholder="URL or upload below"
                                    value={draft.brandmarkUrl}
                                    onChange={(e) => setBrandingField(t.group_id, 'brandmarkUrl', e.target.value)}
                                  />
                                  <div className="mt-2 flex flex-wrap items-center gap-2">
                                    <input
                                      id={`branding-brandmark-file-${t.group_id}`}
                                      type="file"
                                      accept={BRANDING_FILE_ACCEPT}
                                      className="sr-only"
                                      disabled={brandingUploading === `${t.group_id}:brandmark`}
                                      onChange={(e) => {
                                        const f = e.target.files?.[0]
                                        if (f) uploadBrandingAsset(t.group_id, 'brandmark', f)
                                        e.target.value = ''
                                      }}
                                    />
                                    <label
                                      htmlFor={`branding-brandmark-file-${t.group_id}`}
                                      className={`inline-flex cursor-pointer items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 ${
                                        brandingUploading === `${t.group_id}:brandmark` ? 'pointer-events-none opacity-50' : ''
                                      }`}
                                    >
                                      {brandingUploading === `${t.group_id}:brandmark` ? 'Uploading…' : 'Upload file'}
                                    </label>
                                    <span className="text-[11px] text-gray-400">
                                      Stored in tenant Azure Blob Storage (category tenant-branding)
                                    </span>
                                  </div>
                                  {draft.brandmarkUrl?.trim() && (
                                    <img
                                      src={brandingImageSrc(draft.brandmarkUrl.trim())}
                                      alt=""
                                      className="mt-2 h-10 w-10 object-contain object-left rounded"
                                      onError={(e) => { e.target.style.display = 'none' }}
                                    />
                                  )}
                                </div>
                              </div>
                              <div className="grid gap-4 sm:grid-cols-3">
                                {[
                                  { key: 'primaryColor', label: 'Primary' },
                                  { key: 'secondaryColor', label: 'Secondary' },
                                  { key: 'highlightColor', label: 'Highlight' },
                                ].map(({ key, label }) => {
                                  const pv = hexPreview(draft[key])
                                  return (
                                    <label key={key} className="block text-sm">
                                      <span className="font-medium text-gray-700">{label}</span>
                                      <div className="mt-1 flex items-center gap-2">
                                        <input
                                          type="color"
                                          className="h-9 w-10 cursor-pointer rounded border border-gray-300 bg-white p-0.5 shrink-0"
                                          value={pv || '#000000'}
                                          onChange={(e) => setBrandingField(t.group_id, key, e.target.value)}
                                        />
                                        <input
                                          className="flex-1 min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
                                          placeholder="#RRGGBB"
                                          value={draft[key]}
                                          onChange={(e) => setBrandingField(t.group_id, key, e.target.value)}
                                        />
                                      </div>
                                    </label>
                                  )
                                })}
                              </div>
                              <div className="flex flex-wrap gap-2 pt-1">
                                <button
                                  type="button"
                                  onClick={() => saveBranding(t.group_id)}
                                  disabled={brandingSaving === t.group_id}
                                  className="rounded-lg bg-psg-teal px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                                >
                                  {brandingSaving === t.group_id ? 'Saving…' : 'Save branding'}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setBrandingOpen(null)}
                                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    )
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
